import { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import CustomSelect from "~components/CustomSelect";
import type { DataTableFilter } from "~components/DataTable/types/dataTableFilter.ts";
import type { RoleStatuses } from "~enums/roleStatuses.ts";
import type { Roles } from "~enums/roles.ts";
import { TableFilter } from "~enums/tableFilter.ts";
import { getRoleTranslationKey } from "~utilities/getRoleTranslationKey.ts";
import { SeasonOfficialsTableFilters } from "../enums/seasonOfficialsTableFilters";

type SeasonOfficialsFilters = {
  [SeasonOfficialsTableFilters.Role]?: Roles[];
  [SeasonOfficialsTableFilters.Status]?: RoleStatuses;
};

type Props = {
  officialRoles: Roles[];
  roleStatuses: RoleStatuses[];
};

export const useGetSeasonOfficialsTableFilters = ({
  officialRoles,
  roleStatuses,
}: Props): {
  currentFilter: SeasonOfficialsFilters;
  filters: DataTableFilter[];
  handleFilterChange: (updatedFilter: Record<string, unknown>) => void;
} => {
  const { formatMessage } = useIntl();
  const { sport = "" } = useParams();
  const [currentFilter, setCurrentFilter] = useState<SeasonOfficialsFilters>({});

  const handleFilterChange = useCallback((updatedFilter: Record<string, unknown>) => {
    setCurrentFilter(updatedFilter as SeasonOfficialsFilters);
  }, []);

  return {
    currentFilter,
    filters: [
      {
        filterKey: SeasonOfficialsTableFilters.Role,
        tableFilterType: TableFilter.Primary,
        filterElement: ({ value, onChange }) => (
          <CustomSelect
            allowClear
            maxTagCount="responsive"
            mode="multiple"
            onChange={(roles) => onChange(roles as Roles[])}
            options={officialRoles.map((role) => ({
              label: formatMessage({ id: `roles.${getRoleTranslationKey(role, sport)}` }),
              value: role,
            }))}
            placeholder={formatMessage({ id: "role" })}
            value={value}
          />
        ),
      },
      {
        filterKey: SeasonOfficialsTableFilters.Status,
        tableFilterType: TableFilter.Primary,
        filterElement: ({ value, onChange }) => (
          <CustomSelect
            allowClear
            onChange={(status) => onChange(status as RoleStatuses)}
            options={roleStatuses.map((status) => ({
              label: formatMessage({ id: `roleStatus.${status}` }),
              value: status,
            }))}
            placeholder={formatMessage({ id: "status" })}
            value={value}
          />
        ),
      },
    ],
    handleFilterChange,
  };
};
